import { X, Heart, Flame, Award } from 'lucide-react';
import { useEffect } from 'react';
import { useBodyOverflowLock } from '@/hooks/useBodyOverflowLock';

interface FavoritePrayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  name: string;
  hebrewName?: string;
  note?: string;
  prayerType?: string;
  addedByName?: string;
}

// Prayer type configuration - uses purple tint system from PRAYER_CATEGORY_CONFIG
const PRAYER_CONFIG: Record<string, { icon: typeof Heart; label: string; gradient: string }> = {
  healing: { icon: Heart, label: 'Refuah Shleimah', gradient: 'from-[#8e4585] to-[#753a6f]' },
  memory: { icon: Flame, label: 'In Memoriam', gradient: 'from-[#63345e] to-[#4a2847]' },
  dedication: { icon: Award, label: 'Study Dedication', gradient: 'from-[#c4a24e] to-[#a88a3e]' },
};

export function FavoritePrayerModal({
  isOpen,
  onClose,
  name,
  hebrewName,
  note,
  prayerType,
  addedByName,
}: FavoritePrayerModalProps) {
  // Lock body overflow when modal is open
  useBodyOverflowLock(isOpen);

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const config = PRAYER_CONFIG[prayerType || 'healing'] || PRAYER_CONFIG.healing;
  const Icon = config.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${config.gradient} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <Icon size={24} />
              </div>
              <div>
                <span className="text-white/80 text-sm font-medium">{config.label}</span>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Name */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-slate-800">{name}</h2>
            {hebrewName && (
              <p className="text-lg text-slate-600 mt-1" dir="rtl" lang="he">
                {hebrewName}
              </p>
            )}
          </div>

          {/* Note/Dedication */}
          {note && (
            <div className="mb-4">
              <p className="text-slate-600 leading-relaxed">{note}</p>
            </div>
          )}

          {/* Added by */}
          {addedByName && (
            <div className="pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-400">
                Added by <span className="text-slate-600">{addedByName}</span>
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-xl transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
